import { ILevelGeneralListData, levelGeneralListData } from 'src/app/infrastructure/models/level-general-list-data';
import { IMessageListDto, messageListDto } from 'src/assets/common/models/message-list-dto.g';
import { isEmptyObject } from 'src/app/infrastructure/functions/is-empty-object.function';
import { organisationLabel } from 'src/app/infrastructure/functions/user-name.function';
import { utcMoment } from 'src/app/infrastructure/functions/zone-day.function';

// Rij in een berichtenlijst (Project/Domain/Function-tab of het centrale overzicht op Home). requestedDomainUser_guid
// = de eigen DomainUser in het domain van het bericht: daarmee opent de berichtpagina (domaincontext).
// inactive = gelezen en (indien gevraagd) bevestigd: gedimd. De tellers alleen voor afzender en beheer (anders null).
export const messageListData = {
    ...levelGeneralListData,
    requestedDomainUser_guid: 'requestedDomainUser_guid',
    domain_name: 'domain_name',
    project_name: 'project_name',
    task_subject: 'task_subject',
    subject: 'subject',
    sentOn: 'sentOn',
    sender_name: 'sender_name',
    sender_organisation_label: 'sender_organisation_label',
    audience_names: 'audience_names',
    audience_projectMembers: 'audience_projectMembers',
    audience_taskMembers: 'audience_taskMembers',
    acknowledgeRequired: 'acknowledgeRequired',
    mine: 'mine',
    isRecipient: 'isRecipient',
    read: 'read',
    acknowledged: 'acknowledged',
    recipientCount: 'recipientCount',
    readCount: 'readCount',
    acknowledgedCount: 'acknowledgedCount',
} as const;

export type TMessageListData = (typeof messageListData)[keyof typeof messageListData];

export interface IMessageListData extends ILevelGeneralListData {
    [messageListData.requestedDomainUser_guid]: string | null;
    [messageListData.domain_name]: string | null;
    [messageListData.project_name]: string | null;
    [messageListData.task_subject]: string | null;
    [messageListData.subject]: string | null;
    [messageListData.sentOn]: string | null;
    [messageListData.sender_name]: string | null;
    [messageListData.sender_organisation_label]: string | null;
    [messageListData.audience_names]: string | null;
    [messageListData.audience_projectMembers]: boolean;
    [messageListData.audience_taskMembers]: boolean;
    [messageListData.acknowledgeRequired]: boolean;
    [messageListData.mine]: boolean;
    [messageListData.isRecipient]: boolean;
    [messageListData.read]: boolean;
    [messageListData.acknowledged]: boolean;
    [messageListData.recipientCount]: number | null;
    [messageListData.readCount]: number | null;
    [messageListData.acknowledgedCount]: number | null;
}

export class MessageListData
    implements IMessageListData
{
    [messageListData.guid]!: string;
    [messageListData.deleted]: boolean = false;
    [messageListData.inactive]: boolean = false;

    [messageListData.requestedDomainUser_guid]: string | null = null;
    [messageListData.domain_name]: string | null = null;
    [messageListData.project_name]: string | null = null;
    [messageListData.task_subject]: string | null = null;
    [messageListData.subject]: string | null = null;
    [messageListData.sentOn]: string | null = null;
    [messageListData.sender_name]: string | null = null;
    [messageListData.sender_organisation_label]: string | null = null;
    [messageListData.audience_names]: string | null = null;
    [messageListData.audience_projectMembers]: boolean = false;
    [messageListData.audience_taskMembers]: boolean = false;
    [messageListData.acknowledgeRequired]: boolean = false;
    [messageListData.mine]: boolean = false;
    [messageListData.isRecipient]: boolean = false;
    [messageListData.read]: boolean = false;
    [messageListData.acknowledged]: boolean = false;
    [messageListData.recipientCount]: number | null = null;
    [messageListData.readCount]: number | null = null;
    [messageListData.acknowledgedCount]: number | null = null;

    private constructor(
        modelListDto?: IMessageListDto | null
    )
    {
        if (!modelListDto || isEmptyObject(modelListDto)) return;

        this[messageListData.guid] = modelListDto[messageListDto.guid]!;
        this[messageListData.deleted] = modelListDto[messageListDto.deleted] ?? false;
        this[messageListData.inactive] = modelListDto[messageListDto.inactive] ?? false;

        this[messageListData.requestedDomainUser_guid] = modelListDto[messageListDto.requestedDomainUser_Guid] ?? null;
        this[messageListData.domain_name] = modelListDto[messageListDto.domain_Name] ?? null;
        this[messageListData.project_name] = modelListDto[messageListDto.project_Name] ?? null;
        this[messageListData.task_subject] = modelListDto[messageListDto.task_Subject] ?? null;
        this[messageListData.subject] = modelListDto[messageListDto.subject] ?? null;
        // Zoneloos UTC uit de database → ISO met 'Z', anders toont appDate de tijd als lokale tijd.
        this[messageListData.sentOn] = utcMoment(modelListDto[messageListDto.sentOn])?.toISOString() ?? null;
        this[messageListData.sender_name] = modelListDto[messageListDto.sender_Name] ?? null;
        this[messageListData.sender_organisation_label] = organisationLabel(modelListDto[messageListDto.sender_Affiliation], modelListDto[messageListDto.sender_OrganisationName]);
        this[messageListData.audience_names] = modelListDto[messageListDto.audience_Names] || null;
        this[messageListData.audience_projectMembers] = modelListDto[messageListDto.audience_ProjectMembers] ?? false;
        this[messageListData.audience_taskMembers] = modelListDto[messageListDto.audience_TaskMembers] ?? false;
        this[messageListData.acknowledgeRequired] = modelListDto[messageListDto.acknowledgeRequired] ?? false;
        this[messageListData.mine] = modelListDto[messageListDto.mine] ?? false;
        this[messageListData.isRecipient] = modelListDto[messageListDto.isRecipient] ?? false;
        this[messageListData.read] = modelListDto[messageListDto.read] ?? false;
        this[messageListData.acknowledged] = modelListDto[messageListDto.acknowledged] ?? false;
        this[messageListData.recipientCount] = modelListDto[messageListDto.recipientCount] ?? null;
        this[messageListData.readCount] = modelListDto[messageListDto.readCount] ?? null;
        this[messageListData.acknowledgedCount] = modelListDto[messageListDto.acknowledgedCount] ?? null;
    }

    static create(modelListDto?: IMessageListDto): MessageListData | null {
        return modelListDto ? new MessageListData(modelListDto) : null;
    }
}
